/**
 * PermissionsContext — expose `can(module, action)` à partir de la matrice
 * effective renvoyée par /auth/me (`user.rights`, source de vérité unique).
 * Vocabulaire legacy (taches/validations/accuses, edit/update/admin) traduit
 * vers les clés `module.action` de la nouvelle matrice.
 */
import { createContext, useContext, useCallback, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import { hasRight } from "@/components/crm/crmShared";

const PermissionsContext = createContext(null);

const MODULE = { taches: "tasks", tasks: "tasks", documents: "documents", crm: "crm", validations: "avi", accuses: "avi", avi: "avi", pointage: "pointage", settings: "settings", dashboard: "dashboard" };
const ACTION = {
  documents: { view: "view", read: "read", print: "print", create: "create", edit: "edit", update: "edit", admin: "edit", validate: "step_validate", delete: "delete", export: "export", close: "close" },
  tasks: { view: "view", create: "create", edit: "edit", update: "edit", admin: "edit", validate: "edit", delete: "delete", export: "export", close: "close" },
  avi: { view: "view", create: "validation.create", edit: "edit", update: "edit", admin: "edit", validate: "view", delete: "delete", export: "view" },
  crm: { view: "view", create: "lots.create", edit: "edit_sale", update: "edit_sale", admin: "lots.edit", delete: "lots.delete", export: "export" },
  pointage: { view: "view", create: "punch", edit: "correct_validate", update: "correct_validate", admin: "manage_schedules", delete: "manage_schedules", export: "export" },
  settings: { view: "profile" },
  dashboard: { view: "view" },
};

export function PermissionsProvider({ children }) {
  const { user } = useAuth();
  const isFirstAdmin = !!user?.is_first_admin;

  const can = useCallback((module, action) => {
    if (!user) return false;
    const m = MODULE[module] || module;
    const a = ACTION[m]?.[action] || action;
    return hasRight(user, `${m}.${a}`);
  }, [user]);

  const scopeFor = useCallback((module) => user?.rights?.[MODULE[module] || module]?.scope || "own", [user]);

  const value = useMemo(() => ({
    can, scopeFor, loaded: !!user, isFirstAdmin, matrix: user?.rights || {},
  }), [can, scopeFor, user, isFirstAdmin]);

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
}

export function usePermissions() {
  const ctx = useContext(PermissionsContext);
  if (!ctx) return { can: () => false, scopeFor: () => "own", loaded: false, isFirstAdmin: false, matrix: {} };
  return ctx;
}
